/**
 * 실행 상태 → 캔버스 노드 표시.
 * Airflow 태스크 상태(task_map: 노드 id → task_id)를 노드별 상태로 바꾸고, 태스크가 없는 노드(데이터/그룹/제어 통과점)는
 * 흐름상 앞뒤 태스크로부터 유도한다: 소스 데이터는 실행이 시작되면 완료, 그 밖의 데이터 노드는 바로 앞 태스크 상태를 따른다.
 */
import i18n from '../../../i18n';

export type NodeRunStatus = 'waiting' | 'running' | 'success' | 'failed' | 'skipped' | 'stopped';

/** 부분 실행 범위 — only: 고른 노드만, from: 고른 노드부터 끝까지 (노드 id 는 캔버스 id) */
export interface RunScope { mode: 'only' | 'from'; nodes: string[] }

/** 실행 기록의 태스크 한 줄 — 부분 실행이면 범위 밖(out_of_scope)·이어 받음(reused_from) 표시가 붙는다 */
export interface RunTask { task_id: string; state?: string | null; duration?: number | null; out_of_scope?: boolean; reused_from?: string | null }

/** 상태 표시 이름 — 모듈 상수로 두면 언어를 바꿔도 갱신되지 않아 부를 때 번역한다 */
export const RUN_STATUSES: NodeRunStatus[] = ['waiting', 'running', 'success', 'failed', 'skipped', 'stopped'];
export function runStatusLabel(s: NodeRunStatus): string {
  return i18n.t(`common:runStatus.${s}`);
}

/** 노드 id → 태스크 소요시간(초). 태스크가 없는 노드(데이터·통과점)와 아직 끝나지 않은 태스크는 빠진다 */
export function computeNodeDurations(
  taskMap: Record<string, string>, tasks: RunTask[],
): Record<string, number> {
  // 부분 실행에서 돌리지 않은 태스크(범위 밖·이어 받음)는 소요시간이 없다 — '0초' 로 보이면 돈 것처럼 읽힌다
  const byTask = new Map(tasks.filter((t) => !t.out_of_scope && !t.reused_from).map((t) => [t.task_id, t.duration]));
  const out: Record<string, number> = {};
  for (const [nodeId, taskId] of Object.entries(taskMap)) {
    const d = byTask.get(taskId);
    if (typeof d === 'number' && Number.isFinite(d)) out[nodeId] = d;
  }
  return out;
}

/**
 * 노드 id → 최근 실행에서 지나간 건수.
 *
 * 태스크가 있는 노드는 그 태스크가 남긴 건수를 그대로 쓴다. 데이터 노드(SAM파일·DB테이블)는 태스크가 없으므로
 * 이웃에서 가져온다 — 타깃이면 **앞 태스크가 적재한 건수**, 소스면 **뒤 태스크가 읽어 간 건수**가 그 노드를 지나간 양이다.
 */
export function computeNodeRows(
  nodes: any[], edges: any[], taskMap: Record<string, string>, rowsByTask: Record<string, number>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [nodeId, taskId] of Object.entries(taskMap)) {
    const n = rowsByTask[taskId];
    if (typeof n === 'number') out[nodeId] = n;
  }
  const DATA_TYPES = new Set(['csvData', 'rdbData', 'innerData']);
  for (const node of nodes) {
    if (out[node.id] !== undefined || !DATA_TYPES.has(String(node.type))) continue;
    const ups = edges.filter((e: any) => e.target === node.id).map((e: any) => out[e.source]).filter((v: any) => typeof v === 'number');
    const downs = edges.filter((e: any) => e.source === node.id).map((e: any) => out[e.target]).filter((v: any) => typeof v === 'number');
    const near = ups.length ? ups : downs;
    if (near.length) out[node.id] = Math.max(...near);
  }
  return out;
}

/** 소요시간 표시 — utils/formatters 로 옮겼다(언어에 따라 단위가 바뀐다). 기존 import 를 위해 다시 내보낸다 */
export { formatDuration } from '../../../utils/formatters';

const TASK_STATE: Record<string, NodeRunStatus> = {
  success: 'success', running: 'running', failed: 'failed', upstream_failed: 'failed', stopped: 'stopped',
  skipped: 'skipped', queued: 'waiting', scheduled: 'waiting', deferred: 'running', up_for_retry: 'running',
  up_for_reschedule: 'running', restarting: 'running', removed: 'skipped',
};

export function computeNodeRunStatus(
  nodes: any[], edges: any[], taskMap: Record<string, string>,
  tasks: RunTask[], dagState?: string | null,
): Record<string, NodeRunStatus> {
  const taskState = new Map(tasks.map((t) => [t.task_id, t.state || null]));
  // 부분 실행의 범위 밖 태스크 — 표시를 붙이지 않는다('건너뜀' 으로 보이면 분기에서 빠진 것과 헷갈린다).
  // 그 태스크에서만 이어지는 데이터 노드도 함께 비운다.
  const outOfScope = new Set(tasks.filter((t) => t.out_of_scope).map((t) => t.task_id));
  const hidden = new Set<string>();
  for (const n of nodes) if (taskMap[n.id] && outOfScope.has(taskMap[n.id])) hidden.add(n.id);
  const succs = (id: string) => edges.filter((e) => e.source === id).map((e) => e.target);
  const out: Record<string, NodeRunStatus> = {};
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const preds = (id: string) => edges.filter((e) => e.target === id).map((e) => e.source);

  // 1) 태스크가 있는 노드
  for (const n of nodes) {
    const tid = taskMap[n.id];
    if (!tid || hidden.has(n.id)) continue;
    const st = taskState.get(tid);
    out[n.id] = st ? (TASK_STATE[st] || 'waiting') : 'waiting';
  }
  // 2) 태스크 없는 노드: 앞 노드들의 상태로 유도 (제어/데이터/그룹). 재귀 깊이 제한.
  const derive = (id: string, depth = 0): NodeRunStatus => {
    if (out[id]) return out[id];
    if (depth > 50) return 'waiting';
    const ps = preds(id).filter((p) => byId.has(p) && !hidden.has(p));
    if (ps.length === 0) {
      // 소스 데이터: 실행이 시작됐으면 준비 완료. 다만 그것을 읽는 태스크가 모두 범위 밖이면 표시하지 않는다
      const ss = succs(id).filter((x) => byId.has(x));
      if (outOfScope.size && ss.length && ss.every((x) => hidden.has(x))) return 'waiting';
      if (outOfScope.size && preds(id).some((p) => hidden.has(p))) return 'waiting';
      return dagState ? 'success' : 'waiting';
    }
    const sts = ps.map((p) => derive(p, depth + 1));
    if (sts.some((s) => s === 'running')) return 'running';
    if (sts.some((s) => s === 'failed')) return 'failed';
    if (sts.some((s) => s === 'stopped')) return 'waiting';   // 중지 뒤쪽은 돌지 않았다 — 표시 없음
    if (sts.every((s) => s === 'skipped')) return 'skipped';
    if (sts.every((s) => s === 'success' || s === 'skipped')) return 'success';
    return 'waiting';
  };
  for (const n of nodes) {
    if (out[n.id] || hidden.has(n.id)) continue;
    if (n.type === 'memo') continue;
    if (n.type === 'group' || n.type === 'subflow' || n.type === 'repeatGroup') {
      // 그룹: 자식 상태 요약
      const kids = nodes.filter((k) => k.parentId === n.id && !hidden.has(k.id)).map((k) => out[k.id] || derive(k.id));
      if (kids.length) {
        out[n.id] = kids.some((s) => s === 'running') ? 'running' : kids.some((s) => s === 'failed') ? 'failed'
          : kids.every((s) => s === 'success' || s === 'skipped') ? 'success' : 'waiting';
      }
      continue;
    }
    out[n.id] = derive(n.id);
  }
  // 끝난 실행에 남은 '실행대기' 는 표시하지 않는다 — 끝났는데 기다린다고 적혀 있으면 읽을 수가 없다.
  // 실행을 중지했을 때 시작도 못 한 노드가 여기에 해당한다(서버가 기록을 남기지 않는다). 실패로 끝난 경우의
  // 뒤쪽 노드는 서버가 upstream_failed 를 남기므로 '실패' 로 남고, 여기서 지워지지 않는다.
  const stillRunning = new Set(['running', 'queued', 'scheduled', 'restarting', 'up_for_retry']);
  if (dagState && !stillRunning.has(dagState)) {
    for (const k of Object.keys(out)) if (out[k] === 'waiting') delete out[k];
  }
  return out;
}
